// Gemini provider — adapts Google Generative Language API to LLMProvider.
//
// Endpoint: POST https://generativelanguage.googleapis.com/v1beta/models/<model>:streamGenerateContent?alt=sse
// Auth:     header `x-goog-api-key: <key>`
// Streaming format: SSE; each event's `data:` is a JSON GenerateContentResponse.
//
// Key shape differences from our internal Message format:
//   - Gemini uses `role: 'model'` for assistant; we use 'assistant'.
//   - System prompt goes in `systemInstruction`, not in messages[].
//   - Tool calls are `parts[].functionCall = { name, args }`.
//   - Tool results sit in a `parts[].functionResponse = { name, response }`
//     within a 'user' role message — Gemini doesn't have a dedicated
//     tool_result role.
//   - Tools are defined as `tools: [{ functionDeclarations: [...] }]`.
//
// We translate internal ↔ Gemini at the adapter boundary so the rest of
// the loop never sees Gemini-specific shapes.

import type {
  LLMProvider,
  Message,
  ProviderStreamEvent,
  ToolDefinition,
  UsageReport,
} from '../agent/types';

interface GeminiPart {
  text?: string;
  functionCall?: { name: string; args: Record<string, unknown> };
  functionResponse?: {
    name: string;
    response: { content: string; isError?: boolean };
  };
}

interface GeminiContent {
  role: 'user' | 'model';
  parts: GeminiPart[];
}

interface GeminiCandidate {
  content?: GeminiContent;
  finishReason?: string;
}

interface GeminiUsageMetadata {
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  cachedContentTokenCount?: number;
  totalTokenCount?: number;
}

interface GeminiStreamChunk {
  candidates?: GeminiCandidate[];
  usageMetadata?: GeminiUsageMetadata;
}

export interface GeminiProviderOptions {
  apiKey: string;
  /** Defaults to 'gemini-2.5-flash'. Switch to 'gemini-2.5-pro' for deeper reasoning. */
  model?: string;
}

export class GeminiProvider implements LLMProvider {
  readonly slug = 'gemini' as const;
  readonly displayName: string;
  readonly model: string;
  private readonly apiKey: string;

  constructor(opts: GeminiProviderOptions) {
    if (!opts.apiKey?.trim()) {
      throw new Error('GeminiProvider: apiKey is required');
    }
    this.apiKey = opts.apiKey.trim();
    this.model = opts.model ?? 'gemini-2.5-flash';
    this.displayName = `Gemini ${this.model.replace(/^gemini-/, '')}`;
  }

  async streamChat(args: {
    messages: Message[];
    tools: ToolDefinition[];
    signal?: AbortSignal;
    onEvent: (e: ProviderStreamEvent) => void;
  }): Promise<void> {
    const { messages, tools, signal, onEvent } = args;

    const { systemInstruction, contents } = toGeminiContents(messages);
    const requestBody: Record<string, unknown> = { contents };
    if (systemInstruction) requestBody.systemInstruction = systemInstruction;
    if (tools.length > 0) requestBody.tools = [{ functionDeclarations: tools.map(toGeminiTool) }];

    const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(this.model)}:streamGenerateContent?alt=sse`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-goog-api-key': this.apiKey,
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!res.ok || !res.body) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Gemini ${res.status}: ${txt.slice(0, 500)}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Track tool_use state across deltas. Gemini typically delivers
    // each functionCall complete in a single chunk (not progressively
    // accumulated), but we still synthesize start / complete events
    // so the loop's bookkeeping stays uniform with providers that
    // DO stream tool args incrementally.
    let nextToolId = 0;
    const finishedUsage: UsageReport = { inputTokens: 0, outputTokens: 0 };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const dataLine = frame
            .split('\n')
            .find((l) => l.startsWith('data:'))
            ?.slice(5)
            .trim();
          if (!dataLine) continue;
          let chunk: GeminiStreamChunk;
          try {
            chunk = JSON.parse(dataLine);
          } catch {
            continue;
          }
          // Walk candidate parts.
          const cand = chunk.candidates?.[0];
          if (cand?.content?.parts) {
            for (const part of cand.content.parts) {
              if (typeof part.text === 'string' && part.text.length > 0) {
                onEvent({ type: 'text_delta', text: part.text });
              }
              if (part.functionCall) {
                const id = `tu_${nextToolId++}`;
                const name = part.functionCall.name;
                const input = part.functionCall.args ?? {};
                onEvent({ type: 'tool_use_start', id, name });
                onEvent({ type: 'tool_use_complete', id, name, input });
              }
            }
          }
          // Usage typically arrives in the final chunk.
          if (chunk.usageMetadata) {
            finishedUsage.inputTokens = chunk.usageMetadata.promptTokenCount ?? 0;
            finishedUsage.outputTokens = chunk.usageMetadata.candidatesTokenCount ?? 0;
            if (chunk.usageMetadata.cachedContentTokenCount) {
              finishedUsage.cachedInputTokens = chunk.usageMetadata.cachedContentTokenCount;
            }
          }
        }
      }
      onEvent({ type: 'message_complete', usage: finishedUsage });
    } catch (err) {
      // Bubble abort up so the loop's try/catch can surface "已停止".
      if ((err as { name?: string })?.name === 'AbortError') throw err;
      throw err;
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* ignore */
      }
    }
  }
}

// ---- Translation helpers -------------------------------------------------

function toGeminiContents(messages: Message[]): {
  systemInstruction: { parts: GeminiPart[] } | undefined;
  contents: GeminiContent[];
} {
  // Gemini wants system as a separate field, not interleaved.
  const systemTexts: string[] = [];
  const contents: GeminiContent[] = [];

  for (const msg of messages) {
    if (msg.role === 'system') {
      systemTexts.push(msg.content);
      continue;
    }
    if (msg.role === 'user') {
      contents.push({ role: 'user', parts: [{ text: msg.content }] });
      continue;
    }
    if (msg.role === 'assistant') {
      const parts: GeminiPart[] = [];
      for (const block of msg.content) {
        if (block.type === 'text') {
          if (block.text) parts.push({ text: block.text });
        } else if (block.type === 'tool_use') {
          parts.push({
            functionCall: { name: block.name, args: block.input },
          });
        }
      }
      // Empty assistant turns are illegal in Gemini; ensure at least one part.
      if (parts.length === 0) parts.push({ text: '' });
      contents.push({ role: 'model', parts });
      continue;
    }
    if (msg.role === 'tool_result') {
      // Gemini packages tool results as a 'user'-role message containing
      // a functionResponse part. The `name` field must match the tool
      // that was called; we don't have it on ToolResultMessage so we
      // recover it from the immediately preceding assistant tool_use
      // block (the loop never reorders these).
      const name = findToolNameForResult(messages, msg.toolUseId);
      contents.push({
        role: 'user',
        parts: [
          {
            functionResponse: {
              name,
              response: { content: msg.content, isError: msg.isError || undefined },
            },
          },
        ],
      });
      continue;
    }
  }

  return {
    systemInstruction: systemTexts.length > 0
      ? { parts: [{ text: systemTexts.join('\n\n') }] }
      : undefined,
    contents,
  };
}

function findToolNameForResult(messages: Message[], toolUseId: string): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === 'assistant') {
      for (const block of m.content) {
        if (block.type === 'tool_use' && block.id === toolUseId) {
          return block.name;
        }
      }
    }
  }
  return 'unknown_tool';
}

function toGeminiTool(tool: ToolDefinition): {
  name: string;
  description: string;
  parameters: ToolDefinition['inputSchema'];
} {
  return {
    name: tool.name,
    description: tool.description,
    parameters: tool.inputSchema,
  };
}
